import React from 'react';
import { useReactFlow } from 'reactflow';
import {
  Grid,
  Box,
  IconButton,
  ButtonBase,
  Typography,
  Popover,
} from '@mui/material';
import { MoreHorizontalIcon, SixVerticalBoxesIcon } from 'components/SVG';
import ConfirmationDialog from 'components/common/ConfirmationDialog';
import { statusConfigByStatusTag } from '../statusConfigByStatusTag';
import { useDeleteSparkStep } from 'hooks/SparkBuilder/sparkStep/useDeleteSparkStep';
import { useDialog } from 'hooks/common/useDialog';
import { NODE_MIN_WIDTH } from '../../hooks';
import type { SparkButtonEdgeType, SparkStepNodesType } from '../../types';
import type { SparkStepNodeProps } from '../SparkStepNode';
import { useSparkBuilderStore } from 'stores';
import DeleteMemoryQA from 'components/SVG/DeleteMemoryQA';

function CardActionPopover({
  id,
  onDelete,
}: {
  id: string;
  onDelete: (sparkStepID: string | undefined) => Promise<void>;
}) {
  const { deleteElements, getEdges, getNodes } = useReactFlow();

  const {
    open: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog();

  const chartNodes: SparkStepNodesType[] = getNodes();
  const chartEdges: SparkButtonEdgeType[] = getEdges();
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popOverId = open ? id : undefined;

  const onDeleteClick = async () => {
    const sparkStep = chartNodes.find((chartNode) => chartNode.id === id);
    const edgesToDelete: SparkButtonEdgeType[] = chartEdges.filter(
      (edge) => edge.source === sparkStep?.id || edge.target === sparkStep?.id
    );
    if (!sparkStep) return;
    deleteElements({
      nodes: [sparkStep],
      edges: edgesToDelete,
    });

    await onDelete(id);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-describedby={popOverId}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleClick(event);
        }}
      >
        <MoreHorizontalIcon />
      </IconButton>
      <Popover
        id={popOverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <ButtonBase onClick={openDeleteDialog}>
          <Box display='flex' padding={2} gap={2}>
            <DeleteMemoryQA />
            <Typography>Delete</Typography>
          </Box>
        </ButtonBase>
      </Popover>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => {
          closeDeleteDialog();
          handleClose();
        }}
        onPrimaryButtonClick={onDeleteClick}
        title='Confirm Delete'
        confirmationMessage='Are you sure you want to delete this spark step?'
      />
    </>
  );
}

function CustomSparkStepNode({ data }: SparkStepNodeProps) {
  const sparkStep = useSparkBuilderStore(
    React.useCallback(
      (store) => store.sparkStepsActions.getSparkStep(data.stepID || ''),
      [data.stepID]
    )
  );
  const stepType = useSparkBuilderStore(
    React.useCallback(
      (store) =>
        store.stepTypeIdMap?.[sparkStep?.attributes.sparkStepTypeID || ''],
      [sparkStep?.attributes.sparkStepTypeID]
    )
  );

  const handleStepClick = useSparkBuilderStore(
    (store) => store.builderSideBarActions.setSelectedStep
  );
  const [isHovered, setIsHovered] = React.useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const { mutateAsync: deleteSparkStep } = useDeleteSparkStep();

  if (!sparkStep) return null;

  const { attributes: stepAttributes } = sparkStep;
  const sparkDiagnosticStat =
    sparkStep.meta.diagnostics?.hasIssues ||
    sparkStep.meta.diagnostics?.hasMemoryIssues;

  const config =
    statusConfigByStatusTag[sparkDiagnosticStat ? 'warning' : 'success'];

  const Icon = config.Icon;

  const _handleStepClick = () => {
    handleStepClick({ type: 'sparkStep', id: sparkStep.id });
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  const handleDelete = async (sparkStepID: string | undefined) => {
    if (!sparkStepID) {
      return;
    }
    handleStepClick({ type: 'noStepSelected', id: '' });
    await deleteSparkStep(sparkStepID);
  };

  return (
    <>
      <Grid
        container
        className='spark_chart_node'
        id={sparkStep.id}
        component='div'
        sx={{
          backgroundColor: 'white',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: config.borderColor,
          borderRadius: '10px',
          position: 'relative',
          minWidth: `${NODE_MIN_WIDTH}px`,
          margin: '-4px 0px -4px 30px',
          cursor: 'pointer',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        key={sparkStep.id}
      >
        <Grid
          container
          item
          flex={1}
          justifyContent='flex-start'
          alignItems='center'
          gap={2}
          onClick={_handleStepClick}
        >
          <IconButton onPointerDown={handlePointerDown}>
            <Box
              sx={{
                backgroundColor: '#ECEFFE',
                aspectRatio: 1,
                height: '3rem',
                border: '1px solid #EEEEEE',
              }}
              display='flex'
              justifyContent='center'
              alignItems='center'
              margin={2}
            >
              {data?.stepOrder}
            </Box>
          </IconButton>
          <Box
            component='img'
            src={stepType?.attributes.imageURL}
            alt={stepType?.attributes.name}
            sx={{ aspectRatio: 1, width: '42px' }}
          />
          <Typography
            variant='body2'
            sx={{
              width: '600px',
              textWrap: 'pretty',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {stepAttributes.name}
          </Typography>
        </Grid>
        <Grid
          container
          item
          width='5%'
          justifyContent='center'
          alignItems='center'
        >
          <CardActionPopover id={sparkStep.id} onDelete={handleDelete} />
        </Grid>
        <Box sx={{ position: 'absolute', top: -15, right: -15 }}>
          <Icon />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: '33%',
            left: '-25px',
            height: '100%',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.3s ease',
          }}
        >
          <IconButton sx={{ cursor: 'grab' }}>
            <SixVerticalBoxesIcon />
          </IconButton>
        </Box>
      </Grid>
    </>
  );
}

export default CustomSparkStepNode;
